import { useEffect, useState, useMemo, useRef } from "react";
import {
	Edit,
	FormTab,
	useNotify,
	TextInput,
	ArrayInput,
	useGetList,
	TabbedForm,
	useRedirect,
	useTranslate,
	TranslatableInputs,
	SimpleFormIterator,
	NumberInput,
	useUpdate,
	useGetOne,
	SelectInput,
} from "react-admin";
import { userPreferences } from "../../utils/fakeUserPrefrences";
import { CustomToolbar } from "../../customComponents/CustomToolbar";
import { validateSurveyData } from "./surveyValidate";
import { useSurveyTypes } from "../../customHooks/customHooks";
import {
	ManageFeedback,
	checkBracketsOverlap,
	getOverlappingRanks,
	validateBracketsComplete,
	getMissingArabicRanks,
	getInvalidRangeRanks,
	checkBracketsFullCoverage,
	FEEDBACK_MESSAGES_SOURCE,
} from "./ManageFeedback";
import {
	GapWarningDialog,
	CustomTranslatableInputs,
} from "./surveySharedComponents";

export const SurveyEdit = (props) => {
	const { data = {}, loading } = useGetOne("surveys", props.id);
	const [languagesArray, setLanguagesArray] = useState([]);
	const [isActive, setIsActive] = useState(false);
	const [customLanguagesArray, setCustomLanguagesArray] = useState([]);
	const [feedbackBrackets, setFeedbackBrackets] = useState({});
	const [gapWarningOpen, setGapWarningOpen] = useState(false);
	const [pendingData, setPendingData] = useState(null);
	const manageFeedbackRef = useRef();
	const [update] = useUpdate("surveys", props.id);

	const notify = useNotify();
	const redirect = useRedirect();
	const translate = useTranslate();

	const { data: languagesData } = useGetList("languages");
	const { data: surveysData } = useGetList("surveys");
	const { surveyTypes: surveyTypesArray } = useSurveyTypes();

	const filteredSurveyTypes = useMemo(() => {
		const hasActiveGlobalUserFeedback = Object.values(surveysData || {}).some(
			(survey) =>
				survey.id !== data?.id &&
				survey.isActive &&
				survey.surveyType?.type === "Global User Feedback"
		);
		if (hasActiveGlobalUserFeedback) {
			return surveyTypesArray.filter(
				(type) => type.name !== "Global User Feedback"
			);
		}
		return surveyTypesArray;
	}, [surveysData, surveyTypesArray, data?.id]);

	useEffect(() => {
		setCustomLanguagesArray(() => {
			let languages = [];
			Object.values(languagesData).map((language) =>
				language.name === "AR"
					? languages.push("ar(Male)", "ar(Female)")
					: languages.push(language.name.toLowerCase())
			);
			return languages;
		});
		setLanguagesArray(
			Object.values(languagesData).map((language) =>
				language.name.slice(0, 2).toLowerCase()
			)
		);
	}, [languagesData]);

	const defaultLocale = useMemo(
		() => userPreferences.defaultTranslationLanguage,
		[]
	);
	useEffect(() => {
		setIsActive(data.isActive);
	}, [data.isActive]);

	const onSuccess = () => {
		notify(translate("surveys.edit.success"), "info");
		redirect("/surveys");
	};

	const onFailure = () => {
		notify(translate("surveys.edit.fail"), "warning");
	};

	return (
		<>
			{loading ? (
				<center>
					<h1>Loading...</h1>
				</center>
			) : (
				<Edit {...props} title={translate("surveys.edit.title")}>
					<>
						{" "}
						<TabbedForm
							toolbar={<CustomToolbar />}
							submitOnEnter={false}
							validate={validateSurveyData}
							syncWithLocation={false}
							save={(data) => {
								if (Object.keys(feedbackBrackets).length > 0) {
									if (
										!validateBracketsComplete(
											feedbackBrackets
										)
									) {
										notify(
											translate(
												"surveys.feedbackBrackets.incompleteBrackets"
											),
											"warning"
										);
										return;
									}
									const missingArabic =
										getMissingArabicRanks(feedbackBrackets);
									if (missingArabic.length > 0) {
										notify(
											translate(
												"surveys.feedbackBrackets.arabicRequired"
											),
											"warning"
										);
										manageFeedbackRef.current?.markMissingArabic(
											missingArabic
										);
										return;
									}
									manageFeedbackRef.current?.markMissingArabic(
										[]
									);
									const invalidRanges =
										getInvalidRangeRanks(feedbackBrackets);
									if (invalidRanges.length > 0) {
										notify(
											translate(
												"surveys.feedbackBrackets.invalidRange"
											),
											"warning"
										);
										manageFeedbackRef.current?.markOverlapping(
											invalidRanges
										);
										return;
									}
									if (
										checkBracketsOverlap(feedbackBrackets)
									) {
										notify(
											translate(
												"surveys.feedbackBrackets.overlapError"
											),
											"warning"
										);
										const overlappingRanks =
											getOverlappingRanks(
												feedbackBrackets
											);
										manageFeedbackRef.current?.markOverlapping(
											overlappingRanks
										);
										return;
									}
									const maxScore =
										manageFeedbackRef.current?.getMaxScore() ||
										0;
									if (
										!checkBracketsFullCoverage(
											feedbackBrackets,
											maxScore
										)
									) {
										setPendingData(data);
										setGapWarningOpen(true);
										return;
									}
								}
								manageFeedbackRef.current?.markOverlapping([]);
								data.isActive = isActive;
								data.questions = data.allQuestions;
								data.maxScore = manageFeedbackRef.current?.getMaxScore() || 0;
								data.feedbackBrackets =
									Object.values(feedbackBrackets);
								delete data[FEEDBACK_MESSAGES_SOURCE];
								update(
									{ payload: { data } },
									{
										onSuccess,
										onFailure,
									}
								);
							}}
						>
							<FormTab
								label={translate("surveys.tabs.basicInfo")}
							>
								<TranslatableInputs
									locales={languagesArray}
									defaultLocale={defaultLocale}
								>
									<TextInput source="name" />
									<TextInput source="title" />
									<TextInput source="description" multiline />
								</TranslatableInputs>
								<SelectInput
									source="surveyTypeId"
									choices={filteredSurveyTypes}
									optionText="name"
									optionValue="id"
									label="Survey Type"
								/>
							</FormTab>
							<FormTab
								label={translate("surveys.tabs.questions")}
							>
								<ArrayInput source="allQuestions">
									<SimpleFormIterator>
										<CustomTranslatableInputs
											source="title"
											locales={customLanguagesArray}
											label="Question"
										/>
										<ArrayInput source="answers" label= "Answers"> 
											<SimpleFormIterator>
												<CustomTranslatableInputs
													source="title"
													locales={
														customLanguagesArray
													}
													label="Answer"
												/>
												<NumberInput source="weight" label= "Weight"/>
											</SimpleFormIterator>
										</ArrayInput>
									</SimpleFormIterator>
								</ArrayInput>
							</FormTab>
							<FormTab
								label={translate("surveys.tabs.manageFeedback")}
							>
								<ManageFeedback
									ref={manageFeedbackRef}
									record={data}
									locales={customLanguagesArray}
									onChange={setFeedbackBrackets}
								/>
							</FormTab>
						</TabbedForm>
						<GapWarningDialog
							open={gapWarningOpen}
							onClose={() => setGapWarningOpen(false)}
							onConfirm={() => {
								setGapWarningOpen(false);
								if (pendingData) {
									manageFeedbackRef.current?.markOverlapping(
										[]
									);
									pendingData.isActive = isActive;
									pendingData.questions =
										pendingData.allQuestions;
									pendingData.maxScore = manageFeedbackRef.current?.getMaxScore() || 0;
									pendingData.feedbackBrackets =
										Object.values(feedbackBrackets);
									delete pendingData[FEEDBACK_MESSAGES_SOURCE];
									update(
										{ payload: { data: pendingData } },
										{
											onSuccess,
											onFailure,
										}
									);
									setPendingData(null);
								}
							}}
							translate={translate}
						/>
					</>
				</Edit>
			)}
		</>
	);
};
